import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { BlockBlobClient } from '@azure/storage-blob';
import { getExtension } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { UploadProgress } from '../agent.types';

/** Tracks an in-flight upload so it can be cancelled */
interface ActiveUpload {
  id: string;
  abortController: AbortController;
  subject: Subject<UploadProgress>;
}

/**
 * Lightweight upload service for the agent chat.
 * Follows the existing TenantUploadService pattern: SAS token fetch, then BlockBlobClient upload.
 * Provided at component level -- NOT providedIn root.
 */
@Injectable()
export class AgentUploadService implements OnDestroy {

  private static readonly BLOCK_SIZE = 4 * 1024 * 1024;
  private static readonly CONCURRENCY = 10;

  private readonly http = inject(HttpClient);
  private readonly activeUploads = new Map<string, ActiveUpload>();

  /**
   * Upload a single file to Azure Blob Storage via SAS token.
   * Emits progress updates and resolves with a final 'ready' or 'error' state.
   * @param file The file to upload
   * @param trackingId External tracking ID used by the caller to cancel or correlate progress
   */
  public uploadFile(file: File, trackingId: string): Observable<UploadProgress> {

    const subject = new Subject<UploadProgress>();
    const abortController = new AbortController();

    this.activeUploads.set(trackingId, { id: trackingId, abortController, subject });

    // Emit initial uploading state
    subject.next({ id: trackingId, status: 'uploading', progress: 0 });

    // Start the upload asynchronously
    this.executeUpload(trackingId, file, abortController, subject);

    return subject.asObservable();
  }

  /** Cancel an in-flight upload by its tracking id */
  public abort(id: string): void {

    const active = this.activeUploads.get(id);
    if (active) {
      active.abortController.abort();
      active.subject.complete();
      this.activeUploads.delete(id);
    }
  }

  public ngOnDestroy(): void {

    // Abort all pending uploads on service destruction
    for (const active of this.activeUploads.values()) {
      active.abortController.abort();
      active.subject.complete();
    }
    this.activeUploads.clear();
  }

  /** Run the SAS-fetch + blob-upload sequence */
  private async executeUpload(id: string, file: File, abortController: AbortController, subject: Subject<UploadProgress>): Promise<void> {

    try {
      // Generate a unique blob name with the original extension
      const extension = getExtension(file.name) || 'bin';
      const blobName = `${v4()}.${extension}`;

      // Fetch SAS URL from the backend
      const storageAccount = ConfigService.environment.blobRoot;
      const blobUri = encodeURIComponent(`${storageAccount}/${blobName}`);
      const sasUrl = await lastValueFrom(this.http.get(`/api/attachments/sas?bloburi=${blobUri}`, { responseType: 'text' }));

      // Upload via BlockBlobClient
      const blockBlobClient = new BlockBlobClient(sasUrl);
      await blockBlobClient.uploadData(file, {
        blockSize: AgentUploadService.BLOCK_SIZE,
        concurrency: AgentUploadService.CONCURRENCY,
        abortSignal: abortController.signal,
        blobHTTPHeaders: { blobContentType: file.type },
        metadata: { qqfilename: encodeURI(file.name) },
        onProgress: (ev) => {

          const progress = Math.floor((ev.loadedBytes / file.size) * 100);
          subject.next({ id, status: 'uploading', progress });
        },
      });

      // Strip query params (SAS token) to get the clean blob URL
      const blobUrl = sasUrl.split('?')[0];
      subject.next({ id, status: 'ready', progress: 100, blobUrl });
      subject.complete();
    } catch (err: unknown) {
      // Only emit error if not aborted
      if (abortController.signal.aborted) {
        subject.complete();
      } else {
        const message = err instanceof Error ? err.message : 'Upload failed';
        subject.next({ id, status: 'error', progress: 0, error: message });
        subject.complete();
      }
    } finally {
      this.activeUploads.delete(id);
    }
  }
}
